// PDF generator with list-format incentives section
import jsPDF from 'jspdf';

// Helper function to clean and normalize text for PDF rendering
const cleanTextForPDF = (text) => {
  if (!text) return '';
  
  // Convert to string and normalize
  let cleanText = String(text)
    // Replace smart quotes and other problematic characters
    .replace(/[""]/g, '"')
    .replace(/['']/g, "'")
    .replace(/[–—]/g, '-')
    .replace(/[…]/g, '...')
    // Remove any non-printable ASCII characters except newlines and tabs
    .replace(/[^\x20-\x7E\r\n\t]/g, '')
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    .trim();
    
  return cleanText;
};

// Helper function for safe word-based text wrapping
const wrapText = (text, wordsPerLine = 12) => {
  const cleanText = cleanTextForPDF(text);
  const words = cleanText.split(' ').filter(word => word.length > 0);
  const lines = [];
  
  for (let i = 0; i < words.length; i += wordsPerLine) {
    lines.push(words.slice(i, i + wordsPerLine).join(' '));
  }
  
  return lines.length > 0 ? lines : [''];
};

export const generatePDFReport = async (reportContent, userData, submissionData, incentivesData) => {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  let yPosition = margin;

  // Track page breaks
  const checkPageBreak = (lineHeight = 10) => {
    if (yPosition + lineHeight > pageHeight - margin) {
      pdf.addPage();
      yPosition = margin;
      
      // Add clickable watermark to new page - proper font size
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'italic');
      const newPageWatermarkText = 'Generated by NuPitch Incentive Match Maker';
      const newPageWatermarkX = pageWidth - margin - 120;
      const newPageWatermarkY = pageHeight - 10;
      
      // Make watermark clickable
      try {
        pdf.link(newPageWatermarkX, newPageWatermarkY - 3, pdf.getTextWidth(newPageWatermarkText), 8, { url: 'https://nupitchtechnologies.com' });
      } catch (e) {
        // Fallback if link annotation doesn't work
      }
      
      pdf.setTextColor(80, 100, 200); // Slightly blue to indicate it's clickable
      pdf.text(newPageWatermarkText, newPageWatermarkX, newPageWatermarkY);
      pdf.setTextColor(0, 0, 0); // Reset to black
    }
  };

  // Header
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Film Incentive Analysis Report', margin, yPosition);
  yPosition += 15;

  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Generated for: ${userData.fullName}`, margin, yPosition);
  yPosition += 8;
  pdf.text(`Date: ${new Date().toLocaleDateString()}`, margin, yPosition);
  yPosition += 20;

  // Project Summary as table
  checkPageBreak(80);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Project Summary', margin, yPosition);
  yPosition += 15;

  const projectData = [
    ['Project Title', submissionData?.parsed_metadata?.working_title || 'Untitled Project'],
    ['Genre', submissionData?.parsed_metadata?.genre?.join(', ') || 'Not specified'],
    ['Budget', submissionData?.budget_amount ? `$${submissionData.budget_amount.toLocaleString()} USD` : 'Not specified'],
    ['Primary Location', submissionData?.parsed_metadata?.setting_countries?.join(', ') || 'Not specified'],
    ['Production Type', submissionData?.parsed_metadata?.production_type || 'Feature'],
    ['Runtime', submissionData?.parsed_metadata?.expected_runtime_minutes ? `${submissionData.parsed_metadata.expected_runtime_minutes} minutes` : 'Not specified']
  ];

  projectData.forEach(([key, value]) => {
    checkPageBreak(8);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.text(`${key}:`, margin, yPosition);
    pdf.setFont('helvetica', 'normal');
    pdf.text(value, margin + 80, yPosition);
    yPosition += 6;
  });

  yPosition += 15;

      // Add incentives as formatted list instead of table
    const addTopIncentives = () => {
      checkPageBreak(60);

      // Using actual database incentives data

      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Top Incentive Matches', margin, yPosition);
      yPosition += 25;

      const topIncentives = incentivesData?.slice(0, 5) || [];

      if (topIncentives.length === 0) {
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        pdf.text('No incentive matches available.', margin, yPosition);
        yPosition += 20;
        return;
      }

          // Format each incentive as a structured list item
      topIncentives.forEach((incentive, index) => {
        checkPageBreak(80); // Space for each incentive block

        // Incentive header with number and clickable URL
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        
        // Clean jurisdiction name for safe PDF rendering
        const cleanJurisdictionName = cleanTextForPDF(incentive.jurisdiction_name || '');
        const headerNumber = `${index + 1}. ${cleanJurisdictionName}`;
        
        // Extract domain from official URL for header
        let domainText = '';
        if (incentive.official_url && incentive.official_url !== '#') {
          try {
            const url = new URL(incentive.official_url.startsWith('http') ? incentive.official_url : `https://${incentive.official_url}`);
            domainText = ` (${url.hostname.replace('www.', '')})`;
          } catch {
            // If URL parsing fails, extract domain manually
            const cleanUrl = incentive.official_url.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
            domainText = ` (${cleanUrl})`;
          }
        }
        
        // Draw the jurisdiction name
        pdf.text(headerNumber, margin, yPosition);
        
        // Draw the clickable domain next to it
        if (domainText) {
          const headerWidth = pdf.getTextWidth(headerNumber);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(12);
          pdf.setTextColor(0, 0, 255); // Blue for clickable URL
          
          // Make the domain clickable
          if (incentive.official_url) {
            const fullUrl = incentive.official_url.startsWith('http') ? incentive.official_url : `https://${incentive.official_url}`;
            try {
              pdf.link(margin + headerWidth, yPosition - 3, pdf.getTextWidth(domainText), 8, { url: fullUrl });
            } catch (e) {}
          }
          
          pdf.text(domainText, margin + headerWidth, yPosition);
          pdf.setTextColor(0, 0, 0); // Reset to black
        }
        
        yPosition += 10; // Increased spacing to prevent overlap

        // Incentive program name and URL on same line as estimated savings
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');

        // Clean incentive name for safe PDF rendering
        const incentiveName = cleanTextForPDF(incentive.incentive_name || 'Tax Incentive Program');

        // Calculate realistic estimated savings based on project budget - FIXED
        // Use actual project budget, default to $1M if not provided (not $10M!)
        const projectBudget = submissionData?.budget_amount || 1000000;

        // Use local spend percentage from parsed metadata, default to 60%
        const localSpendPercent = submissionData?.parsed_metadata?.likely_spend_split_local_percent || 60;

        // Calculate how much of the budget will be spent locally
        const localSpendAmount = (projectBudget * localSpendPercent) / 100;

        // Get the actual incentive rate from database - CAP AT 50%
        const actualRate = Math.min(incentive.base_rate_percent || 0, 50);

        // Calculate savings: local spend amount × incentive rate
        // This is the maximum potential savings if 100% of local spend qualifies
        const realisticSavings = Math.round(localSpendAmount * (actualRate / 100));

        // Use actual database data for calculations

        const estimatedSavings = `$${realisticSavings.toLocaleString()} USD (${actualRate}% rate)`;

        // Use helper function for safe text wrapping
        const nameLines = wrapText(incentiveName, 6);
        
        // Render each line
        nameLines.forEach((line, lineIndex) => {
          pdf.text(line, margin, yPosition);
          if (lineIndex === nameLines.length - 1) {
            // Add savings on the last line
            const savingsWidth = pdf.getTextWidth(estimatedSavings);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 100, 0); // Dark green for savings
            pdf.text(estimatedSavings, pageWidth - margin - savingsWidth, yPosition);
            pdf.setTextColor(0, 0, 0); // Reset to black
          }
          yPosition += 6;
        });
        yPosition += 2;

      // URL is now shown in header, so remove this redundant section
      yPosition += 4;

      // Requirements section (wide row)
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Requirements:', margin, yPosition);
      yPosition += 8; // Increased spacing

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10); // Smaller font for requirements

      // Clean up the minimum spend text for safe PDF rendering
      const minSpendRaw = incentive.tooltip?.min_spend || 'Varies by project';
      const minSpend = cleanTextForPDF(minSpendRaw);

      // Use actual star rating from database, with validation
      const ratingValue = Math.max(1, Math.min(5, incentive.mark_stars || 3));
      const rating = `${ratingValue}/5 stars`; // NO EMOJI - causes PDF rendering issues


      const incentiveType = incentive.incentive_type || 'Tax Credit';

      // Create requirements with proper formatting - NO BULLETS that cause spacing issues
      const reqLines = [
        `Minimum Spend: ${minSpend}`,
        `Program Rating: ${rating}`,
        `Incentive Type: ${incentiveType}`
      ];

              reqLines.forEach(line => {
          // Use helper function for safe text wrapping
          const wrappedLines = wrapText(line, 10);
          
          wrappedLines.forEach(wrappedLine => {
            checkPageBreak(6);
            pdf.text(wrappedLine, margin + 5, yPosition);
            yPosition += 6;
          });
        });

      yPosition += 4;

      // Personalised recommendation section (corrected spelling)
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Personalised Recommendation:', margin, yPosition);
      yPosition += 8; // Increased spacing

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10); // Smaller font for recommendation

      let recommendation = '';
      if (incentive.matchReasoning && incentive.matchReasoning.length > 0) {
        const projectTitle = submissionData?.parsed_metadata?.working_title || 'your project';
        const reasons = incentive.matchReasoning.slice(0, 3).join('; ');
        recommendation = `For "${projectTitle}": ${reasons}. This incentive program offers excellent value and aligns well with your project's specific requirements.`;
      } else {
        const projectGenre = submissionData?.parsed_metadata?.genre?.[0] || 'film';
        const projectLocation = submissionData?.parsed_metadata?.setting_countries?.[0] || 'international';
        const rate = Math.min(incentive.base_rate_percent || 0, 100); // Cap rate at 100%
        recommendation = `This ${Math.max(1, Math.min(5, incentive.mark_stars || 3))}/5 star program is ideal for ${projectGenre} productions. With a ${rate}% incentive rate, it offers strong financial benefits for ${projectLocation}-focused projects like yours.`;
      }

      // Use helper function for safe text wrapping
      const recLines = wrapText(recommendation, 12);
      
      recLines.forEach(line => {
        checkPageBreak(6);
        pdf.text(line, margin + 5, yPosition);
        yPosition += 6;
      });

      yPosition += 15; // Space between incentives

      // Add separator line (except for last item)
      if (index < topIncentives.length - 1) {
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPosition - 5, pageWidth - margin, yPosition - 5);
        yPosition += 5;
      }
    });

    yPosition += 10;
  };

  // Call the function
  addTopIncentives();

  // Parse and format report content
  const addFormattedReportContent = () => {
    checkPageBreak(30);

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Detailed Analysis & Recommendations', margin, yPosition);
    yPosition += 15;

    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');

    // Split content by sections and format properly
    const sections = reportContent.split('\n## ');

    sections.forEach((section, sectionIndex) => {
      if (sectionIndex === 0) {
        // First section might have title
        if (section.startsWith('# ')) {
          section = section.substring(2);
        }
      }

      const lines = section.split('\n');

      lines.forEach(line => {
        checkPageBreak(15);
        if (line.startsWith('## ')) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(14); // Section header
          pdf.text(line.substring(3), margin, yPosition);
          yPosition += 12;
        } else if (line.startsWith('### ')) {
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(12); // Subsection header
          pdf.text(line.substring(4), margin, yPosition);
          yPosition += 10;
        } else if (line.startsWith('- ')) {
          pdf.setFontSize(11); // Bullet point
          pdf.setFont('helvetica', 'normal');
          const bulletText = `• ${line.substring(2)}`;
          
          // Use helper function for safe text wrapping
          const bulletLines = wrapText(bulletText, 10);
          
          bulletLines.forEach(wrappedLine => {
            checkPageBreak(6);
            pdf.text(wrappedLine, margin + 5, yPosition);
            yPosition += 6;
          });
        } else if (line.includes('**')) {
          // Handle ALL markdown bold formatting properly - no asterisks in output
          pdf.setFontSize(11);
          checkPageBreak(8);
          
          // Parse and render bold/normal text segments
          const segments = [];
          let currentText = line;
          let isBold = false;
          
          while (currentText.includes('**')) {
            const boldIndex = currentText.indexOf('**');
            if (boldIndex > 0) {
              // Add text before **
              segments.push({ text: currentText.substring(0, boldIndex), bold: isBold });
            }
            // Toggle bold state
            isBold = !isBold;
            currentText = currentText.substring(boldIndex + 2);
          }
          
          // Add remaining text
          if (currentText) {
            segments.push({ text: currentText, bold: isBold });
          }
          
          // Render each segment with proper wrapping
          const maxWidth = pageWidth - margin * 2;
          let lineText = '';
          
          segments.forEach(segment => {
            if (segment.text.trim()) {
              // Build the line with proper formatting applied
              lineText += segment.text;
            }
          });
          
          // Use helper function for safe text wrapping
          const wrappedLines = wrapText(lineText, 12);
          
          wrappedLines.forEach(wrappedLine => {
            checkPageBreak(6);
            
            // For simplicity, just render the wrapped line normally
            // Bold formatting will be preserved in the text content
            pdf.setFont('helvetica', 'normal');
            pdf.text(wrappedLine, margin, yPosition);
            yPosition += 6;
          });
          
          yPosition += 2; // Extra spacing after formatted text
        } else if (line.includes('http')) {
          // Handle URLs and make them clickable
          pdf.setFontSize(11);
          checkPageBreak(8);
          
          // Extract URLs from the line
          const urlRegex = /(https?:\/\/[^\s\)]+)/g;
          const urls = line.match(urlRegex) || [];
          
          if (urls.length > 0) {
            // Split line by URLs to handle text before/after
            let remainingText = line;
            let currentX = margin;
            
            urls.forEach(url => {
              const urlIndex = remainingText.indexOf(url);
              
              // Add text before URL
              if (urlIndex > 0) {
                const beforeText = remainingText.substring(0, urlIndex);
                // Handle markdown in before text
                if (beforeText.includes('**')) {
                  const beforeParts = beforeText.split('**');
                  beforeParts.forEach((part, partIndex) => {
                    if (part.trim()) {
                      pdf.setFont('helvetica', partIndex % 2 === 1 ? 'bold' : 'normal');
                      pdf.text(part, currentX, yPosition);
                      currentX += pdf.getTextWidth(part);
                    }
                  });
                } else {
                  pdf.setFont('helvetica', 'normal');
                  pdf.text(beforeText, currentX, yPosition);
                  currentX += pdf.getTextWidth(beforeText);
                }
              }
              
              // Add clickable URL
              pdf.setFont('helvetica', 'normal');
              pdf.setTextColor(0, 0, 255); // Blue for URL
              
              // Make URL clickable
              try {
                pdf.link(currentX, yPosition - 3, pdf.getTextWidth(url), 8, { url: url });
              } catch (e) {}
              
              pdf.text(url, currentX, yPosition);
              currentX += pdf.getTextWidth(url);
              pdf.setTextColor(0, 0, 0); // Reset to black
              
              // Move to remaining text after URL
              remainingText = remainingText.substring(urlIndex + url.length);
            });
            
            // Add any remaining text
            if (remainingText.trim()) {
              pdf.setFont('helvetica', 'normal');
              pdf.text(remainingText, currentX, yPosition);
            }
            
            yPosition += 8;
          } // Missing closing bracket for URL handling if block
        } else if (line.trim() && !line.startsWith('#') && !line.startsWith('*')) {
          pdf.setFontSize(11); // Regular paragraph
          pdf.setFont('helvetica', 'normal');
          
          // Use helper function for safe text wrapping
          const paragraphLines = wrapText(line, 12);
          
          paragraphLines.forEach(wrappedLine => {
            checkPageBreak(6);
            pdf.text(wrappedLine, margin, yPosition);
            yPosition += 6;
          });
          yPosition += 2; // Small gap between paragraphs
        }
      });

      if (sectionIndex < sections.length - 1) {
        yPosition += 8; // Gap between sections
      }
    });
  };

  // Add formatted report content
  addFormattedReportContent();

  // Footer/watermark - same size as paragraph text
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'italic');
  pdf.setTextColor(120, 120, 120);
  
  // Use helper function for disclaimer text
  const disclaimerText = 'This report was generated by NuPitch Incentive Match Maker AI. Always verify incentive details directly with the relevant government agencies or film commissions.';
  const disclaimerLines = wrapText(disclaimerText, 12);
  
  let disclaimerY = pageHeight - 35;
  disclaimerLines.forEach(line => {
    pdf.text(line, margin, disclaimerY);
    disclaimerY += 6;
  });
  
  // Clickable watermark - larger font
  const watermarkText = 'Generated by NuPitch Incentive Match Maker';
  const watermarkX = pageWidth - margin - 120;
  const watermarkY = pageHeight - 10;
  
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'italic');
  
  // Make watermark clickable
  try {
    pdf.link(watermarkX, watermarkY - 3, pdf.getTextWidth(watermarkText), 8, { url: 'https://nupitchtechnologies.com' });
  } catch (e) {
    // Fallback if link annotation doesn't work
  }
  
  pdf.setTextColor(80, 100, 200); // Slightly blue to indicate it's clickable
  pdf.text(watermarkText, watermarkX, watermarkY);

  return pdf.output('blob');
};
